// modules/inicio/offline/crudOfflineFactory.js
//
// Motor genérico de guardado offline con soporte real de crear/editar/
// eliminar, sin duplicar pendientes. Lo usan discapacidadOffline.js y
// vejezOffline.js (y cualquier estrategia nueva) — así la lógica correcta
// vive en un solo lugar en vez de reescribirse (con el riesgo de
// reescribirla mal) por cada estrategia.
//
// Reglas clave (esto es lo que evita pérdida/duplicación de información):
//
// 1. Si editas un registro que TODAVÍA es local (nunca se sincronizó —
//    su id visible es "local_<n>"), NO se crea un pendiente nuevo: se
//    actualiza el MISMO pendiente de creación. Sin esto, editar dos veces
//    antes de tener señal generaba dos pendientes: uno de "crear" y otro
//    de "editar" apuntando a un id falso que el servidor nunca reconoce.
//
// 2. Si editas un registro que YA existe en el servidor y ya habías hecho
//    otra edición offline sin sincronizar todavía, se actualiza ese mismo
//    pendiente en vez de apilar uno nuevo (solo importa la versión más
//    reciente).
//
// 3. Si eliminas un registro que todavía es local, se borra directo de la
//    cola — nunca llegó a existir en el servidor, no hay nada que avisarle.
//
// 4. Si eliminas un registro que ya existe en el servidor, cualquier
//    edición pendiente de ese mismo registro queda obsoleta (se va a
//    borrar de todas formas) — se descarta esa edición y se encola solo
//    la eliminación.
//
// 5. Al sincronizar: si el servidor responde 404 al editar/eliminar (el
//    registro ya no existe allá — alguien más lo borró, por ejemplo), no
//    se reintenta para siempre: se da por sincronizado, con el motivo
//    anotado en ultimoError para que quede trazado qué pasó.

import db from "../../../offline/db";

// "local_123" -> 123 (el id real dentro de Dexie del pendiente de
// creación). Si no es un id local, devuelve null.
export function extraerIdLocal(id) {
  if (typeof id === "string" && id.startsWith("local_")) {
    const n = parseInt(id.replace("local_", ""), 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function crearCrudOffline({ tablaPendientes, apiCrear, apiActualizar, apiEliminar }) {
  const tabla = () => db[tablaPendientes];

  // `personaId` es el id que ya tenía el registro (real del servidor, o
  // "local_N" si todavía no se sincronizaba) — undefined/null si es una
  // creación nueva.
  async function guardarOffline(payload, personaId) {
    const idLocal = extraerIdLocal(personaId);

    if (idLocal != null) {
      await tabla().update(idLocal, { ...payload, _tipo: "crear", sincronizado: 0, ultimoError: null });
      return;
    }

    if (personaId != null) {
      const existentes = await tabla()
        .filter(r => r.sincronizado === 0 && r._tipo === "editar" && r._editId === personaId)
        .toArray();
      if (existentes.length) {
        await tabla().update(existentes[0].id, { ...payload, _tipo: "editar", _editId: personaId, sincronizado: 0, ultimoError: null });
        return;
      }
      await tabla().add({ ...payload, _tipo: "editar", _editId: personaId, sincronizado: 0, ultimoError: null, createdAt: new Date().toISOString() });
      return;
    }

    await tabla().add({ ...payload, _tipo: "crear", sincronizado: 0, ultimoError: null, createdAt: new Date().toISOString() });
  }

  async function eliminarOffline(personaId) {
    const idLocal = extraerIdLocal(personaId);
    if (idLocal != null) {
      await tabla().delete(idLocal);
      return;
    }

    const obsoletos = await tabla()
      .filter(r => r.sincronizado === 0 && r._tipo === "editar" && r._editId === personaId)
      .toArray();
    for (const o of obsoletos) await tabla().delete(o.id);

    await tabla().add({ _tipo: "eliminar", _editId: personaId, sincronizado: 0, ultimoError: null, createdAt: new Date().toISOString() });
  }

  async function contarPendientes() {
    return tabla().where("sincronizado").equals(0).count();
  }

  // No dispara "sync-completado" acá — lo hace una sola vez el motor
  // central (syncRegistry.js) después de sincronizar TODAS las entidades.
  async function sincronizarPendientes() {
    const pendientes = await tabla().filter(r => r.sincronizado === 0).toArray();
    let sincronizados = 0;

    for (const registro of pendientes) {
      const { id: localId, sincronizado, ultimoError, createdAt, _editId, _tipo, ...payload } = registro;
      try {
        if (_tipo === "eliminar") await apiEliminar(_editId);
        else if (_tipo === "editar") await apiActualizar(_editId, payload);
        else await apiCrear(payload);

        await tabla().update(localId, { sincronizado: 1 });
        sincronizados++;
      } catch (error) {
        const status = error?.response?.status;

        if (status === 404 && _tipo !== "crear") {
          // El registro ya no existe en el servidor — no tiene caso
          // reintentar por siempre.
          await tabla().update(localId, { sincronizado: 1, ultimoError: "El registro ya no existía en el servidor" });
          sincronizados++;
          continue;
        }

        const esErrorDeDatos = [400, 409, 413, 422].includes(status);
        await tabla().update(localId, {
          sincronizado: esErrorDeDatos ? 3 : 0,
          ultimoError: error?.response?.data?.message || error.message,
        });
      }
    }
    return sincronizados;
  }

  return { guardarOffline, eliminarOffline, sincronizarPendientes, contarPendientes };
}
